import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import useFetchCart from './FetchCart';
import { useCart } from './CartContext';
import './ReviewOrder.css';
import BASE_URL, { getImageURL } from '../config';

const ReviewOrder = () => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { type: 'success'|'error', text: '...' }
  const stripe = useStripe();
  const elements = useElements();
  const { cart } = useFetchCart();
  const { fetchCart } = useCart();
  const navigate = useNavigate();

  const [tAmount, settAmount] = useState(0);
  const [shipaddress, setShipaddress] = useState(null);
  const [billaddress, setBilladdress] = useState(null);
  const [deltime, setDeltime] = useState(null);
  const [deldate, setDeldate] = useState(null);
  const paymentmethod = "stripe";
  const [deliveryFee, setDeliveryFee] = useState(0);

  const stripeElementStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '10px 15px',
      },
    },
  };

  useEffect(() => {
    const storedOrderData = JSON.parse(localStorage.getItem('orderData'));
    if (storedOrderData) {
      setOrderData(storedOrderData);
      setShipaddress(storedOrderData.shipping_address);
      setBilladdress(storedOrderData.shipping_address);
      const formattedDate = new Date(storedOrderData.delivery_date).toISOString().split('T')[0];
      setDeldate(formattedDate);
      setDeltime(storedOrderData.delivery_time);
      settAmount(storedOrderData.total);
      setDeliveryFee(storedOrderData.delivery_fee ?? parseFloat(localStorage.getItem('deliveryFee') || 0));
    } else {
      navigate('/checkout');
    }
  }, [navigate]);
  
  const handlePayment = async () => {
    try {
      setLoading(true);
      setStatusMessage(null);

      const paymentResponse = await axios.post(`${BASE_URL}/api/payment/`, {
        amount: orderData.total
      });

      const { client_secret } = paymentResponse.data;

      if (!stripe || !elements) {
        setLoading(false);
        setStatusMessage({ type: 'error', text: 'Stripe has not been initialized. Please refresh and try again.' });
        return;
      }

      const cardNumber = elements.getElement(CardNumberElement);
      const cardExpiry = elements.getElement(CardExpiryElement);
      const cardCvc = elements.getElement(CardCvcElement);

      if (!cardNumber || !cardExpiry || !cardCvc) {
        setLoading(false);
        setStatusMessage({ type: 'error', text: 'Card elements not found. Please refresh and try again.' });
        return;
      }

      const paymentResult = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumber
        }
      });

      if (paymentResult.error) {
        setStatusMessage({ type: 'error', text: paymentResult.error.message });
        setLoading(false);
      } else if (paymentResult.paymentIntent.status === 'succeeded') {
        await finalizeCheckout();
      }
    } catch (error) {
      console.error('Payment error:', error);
      setStatusMessage({ type: 'error', text: 'Payment failed. Please try again.' });
      setLoading(false);
    }
  };

  const finalizeCheckout = async () => {
    const checkoutData = {
      shipping_address: shipaddress,
      delivery_date: deldate,
      delivery_time: deltime,
      payment_method: paymentmethod
    };

    const authToken = localStorage.getItem('authToken');

    try {
      const checkoutResponse = await axios.post(`${BASE_URL}/api/checkout/`, checkoutData, {
        headers: {
          'Authorization': `Token ${authToken}`
        }
      });
      
      const order_id = checkoutResponse.data.id;
      localStorage.removeItem('orderData');
      fetchCart(); // Clear the cart badge in the navbar
      navigate(`/ordersummary/${order_id}`);
    } catch (error) {
      console.error('Checkout error:', error?.response?.data || error);
      const serverError = error?.response?.data?.error || 'Unknown error';
      setStatusMessage({ type: 'error', text: `Payment was successful but order creation failed: ${serverError}. Please contact support.` });
    } finally {
      setLoading(false);
    }
  };

  // Helper to safely get item price
  const getItemPrice = (item) => {
    const price = parseFloat(item.discount_price) || parseFloat(item.price) || 0;
    return price;
  };

  if (!orderData || !cart) return <div className="review-loading">Loading...</div>;

  return (
    <div className="review-page">
      <h1>Review Your Order</h1>

      {/* ── Status message (replaces ugly alert) ── */}
      {statusMessage && (
        <div className={`review-status ${statusMessage.type}`}>
          {statusMessage.type === 'success' ? '✅ ' : '⚠️ '}
          {statusMessage.text}
        </div>
      )}

      <div className="review-grid">
        {/* ── Left column: Order details ── */}
        <div>
          {/* Delivery information */}
          <div className="review-section">
            <h2 className="review-section-title">Delivery Information</h2>
            <div className="review-info-card">
              <div className="review-info-row">
                <span className="review-info-label">Billing address</span>
                <span className="review-info-value">
                  {orderData.billing_address?.address_line1}
                  {orderData.billing_address?.address_line2 ? `, ${orderData.billing_address.address_line2}` : ''}
                  <br />
                  {orderData.billing_address?.postal_code} {orderData.billing_address?.city}
                  <br />
                  {orderData.billing_address?.country}
                </span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Shipping address</span>
                <span className="review-info-value">
                  {orderData.shipping_address?.address_line1}
                  {orderData.shipping_address?.address_line2 ? `, ${orderData.shipping_address.address_line2}` : ''}
                  <br />
                  {orderData.shipping_address?.postal_code} {orderData.shipping_address?.city}
                  <br />
                  {orderData.shipping_address?.country}
                </span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Delivery type</span>
                <span className="review-info-value">{orderData.delivery_type}</span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Delivery date</span>
                <span className="review-info-value">{deldate}</span>
              </div>
              <div className="review-info-row">
                <span className="review-info-label">Delivery time</span>
                <span className="review-info-value">{orderData.delivery_time}</span>
              </div>
            </div>
          </div>

          {/* Cart items */}
          <div className="review-section">
            <h2 className="review-section-title">Your Cart</h2>
            {cart.items.map(item => (
              <div className="review-item" key={item.id}>
                <img
                  className="review-item-image"
                  src={item.item.mainimage ? getImageURL(item.item.mainimage) : 'placeholder.jpg'}
                  alt={item.item.title}
                />
                <div className="review-item-info">
                  <p className="review-item-title">{item.item.title}</p>
                  <span className="review-item-qty">Qty: {item.quantity}</span>
                </div>
                <span className="review-item-price">
                  {(getItemPrice(item.item) * item.quantity).toFixed(2)} CHF
                </span>
              </div>
            ))}

            <div className="review-totals">
              <div className="review-total-row">
                <span>Subtotal</span>
                <span>{(parseFloat(tAmount || 0) - parseFloat(deliveryFee || 0)).toFixed(2)} CHF</span>
              </div>
              <div className="review-total-row">
                <span>Delivery fee</span>
                <span>{parseFloat(deliveryFee || 0) === 0
                  ? <span style={{color:'#2e7d32', fontWeight:600}}>Free</span>
                  : `${parseFloat(deliveryFee || 0).toFixed(2)} CHF`}
                </span>
              </div>
              <div className="review-total-row tax">
                <span>Total excl. VAT</span>
                <span>{(parseFloat(tAmount || 0) / 1.081).toFixed(2)} CHF</span>
              </div>
              <div className="review-total-row tax">
                <span>VAT (8.1%)</span>
                <span>{(parseFloat(tAmount || 0) - parseFloat(tAmount || 0) / 1.081).toFixed(2)} CHF</span>
              </div>
              <div className="review-total-row grand">
                <span>Total</span>
                <span>{parseFloat(tAmount || 0).toFixed(2)} CHF</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right column: Payment ── */}
        <div>
          <div className="review-payment">
            <h2 className="review-payment-title">Payment</h2>

            <div className="review-method-tab">
              <span className="review-method-icon">💳</span>
              <span>Card</span>
            </div>

            <div className="review-field">
              <label>Card number</label>
              <div className="review-field-input">
                <CardNumberElement options={stripeElementStyle} />
              </div>
            </div>

            <div className="review-card-row">
              <div className="review-field">
                <label>Expiration date</label>
                <div className="review-field-input">
                  <CardExpiryElement options={stripeElementStyle} />
                </div>
              </div>
              <div className="review-field">
                <label>Security code</label>
                <div className="review-field-input">
                  <CardCvcElement options={stripeElementStyle} />
                </div>
              </div>
            </div>

            <p className="review-legal-notice">
              By placing this order, you accept our{' '}
              <a href="/legal" target="_blank" rel="noreferrer">General Terms and Conditions</a>{' '}
              and acknowledge our{' '}
              <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </p>

            <button
              onClick={handlePayment}
              className="review-pay-btn"
              disabled={loading}
            >
              {loading ? 'Processing...' : `Pay ${parseFloat(tAmount || 0).toFixed(2)} CHF`}
            </button>

            <a href="/checkout" className="review-back-link">← Back to checkout</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewOrder;